import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBaseUrl } from './ApiContext';
import './CompanyResearch.css';

const baseUrl = getBaseUrl();

// What the company itself said, out of its decks and its own messages to
// us. Every line names where it came from, because "Series A at $60m post"
// is a different claim depending on whether a 2024 PDF or last week's
// email said it, and a reader who is told which can weigh two lines that
// disagree instead of having to pick one.
function Finding({ finding, uncitedNote }) {
    const tag = [finding.source, finding.ref, finding.detail]
        .filter(Boolean).join(' · ');
    return (
        <li className="cr-finding">
            <div className="cr-statement">{finding.statement}</div>
            {finding.quote && (
                <blockquote className="cr-quote">{finding.quote}</blockquote>
            )}
            {/* Not decoration. A deck with no text layer was read from the
                page images, so the statement is nobody's quotation and is
                the only thing here that cannot be checked against a
                source. It looks identical to a checked line without this. */}
            {!finding.quote && finding.uncited && (
                <div className="cr-uncited" title={uncitedNote}>
                    read from the page images, not a quotation
                </div>
            )}
            <div className="cr-source">{tag}</div>
        </li>
    );
}

function provenance(research) {
    const bits = [];
    const read = research.decks.filter(d => d.read).length;
    if (read) {
        bits.push(`${read} deck${read === 1 ? '' : 's'} read`);
    } else if (research.decks.length) {
        bits.push('deck on file, not readable');
    } else {
        bits.push('no deck on file');
    }
    if (!research.mail.read) {
        bits.push('their mail has not been read');
    } else if (!research.mail.message_count) {
        bits.push('nothing from their own domain in our mailboxes');
    } else {
        const n = research.mail.message_count;
        bits.push(`${n} message${n === 1 ? '' : 's'} they wrote`
            + (research.mail.latest_message_at
                ? `, latest ${research.mail.latest_message_at}` : ''));
    }
    return bits.join(' · ');
}

function CompanyResearch({ companyId }) {
    const [research, setResearch] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!companyId) return;
        let live = true;
        setResearch(null);
        setError(null);
        axios.get(`${baseUrl}/api/companies/${companyId}/research`)
            .then(response => { if (live) setResearch(response.data); })
            .catch(err => {
                console.error('Error fetching company research:', err);
                // The panel reports its own failure. The rest of the page
                // is a different set of endpoints and still renders.
                if (live) setError('Could not load research for this company.');
            });
        return () => { live = false; };
    }, [companyId]);

    if (!companyId) return null;
    if (error) return <div className="cr-panel cr-note">{error}</div>;
    if (!research) return <div className="cr-panel cr-note">Loading research...</div>;

    return (
        <div className="cr-panel">
            <div className="cr-head">
                <h3>What their deck and their mail say</h3>
                <div className="cr-provenance">{provenance(research)}</div>
            </div>

            {!research.any_findings ? (
                // One sentence, not five identical ones. A company nobody
                // has read yet says so once; the per-dimension reasons
                // only earn their space when they differ.
                <div className="cr-note">
                    Nothing has been read for this company yet.
                </div>
            ) : research.dimensions.map(dimension => (
                <div className="cr-dimension" key={dimension.key}>
                    <div className="cr-label">{dimension.label}</div>
                    {dimension.findings.length ? (
                        <ul className="cr-findings">
                            {dimension.findings.map((finding, i) => (
                                <Finding key={`${dimension.key}-${i}`}
                                    finding={finding}
                                    uncitedNote={research.uncited_note} />
                            ))}
                        </ul>
                    ) : (
                        // Why it is empty, in the words that separate "we
                        // never looked" from "we looked and they never
                        // said". One blank space for both teaches a reader
                        // to distrust the whole panel.
                        <div className="cr-empty">{dimension.empty_reason}</div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default CompanyResearch;
